import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  IMailrailAutoSendEmailCampaign,
  MailrailAutoSendEmailCampaign,
  MailrailBaseEmailCampaign,
  TUnitOfTime,
} from '@feathr/blackbox';
import {
  CardContent,
  CardHeader,
  CardV2,
  Checkbox,
  NumberInput,
  Radios,
  Select,
} from '@feathr/components';
import { useFlags } from '@feathr/extender/state';
import { flattenError } from '@feathr/hooks';
import type { IValidateGrouped } from '@feathr/rachis';

import type { ITimeUnitOption } from '../MailrailCampaignEdit/timeUnits.utils';
import {
  bestUnit,
  getTimeUnitOptions,
  getTimeUnitOptionValue,
  minimumDisplayValues,
  toDisplayValue,
  toSeconds,
} from '../MailrailCampaignEdit/timeUnits.utils';
import MailrailBulkSendConfig from './MailrailBulkSendConfig';

import * as styles from './MailrailCampaignSendingOptions.css';

interface IProps {
  campaign: MailrailBaseEmailCampaign;
  disabled: boolean;
  validationErrors: IValidateGrouped;
}

function MailrailCampaignSendingOptions({
  campaign,
  disabled,
  validationErrors,
}: Readonly<IProps>): JSX.Element {
  const flags = useFlags();
  const isDripOrAuto = campaign.isMailrailAutoSend || campaign.isMailrailDrip;
  const isTimeSubtype = isDripOrAuto && campaign.get('subtype') === 'time';
  const includedSegmentIds = campaign.get('segments_included', []).filter(Boolean);
  const repeatAllowed = campaign.get('repeat_allowed', false);
  const [cooldownUnit, setCooldownUnit] = useState<TUnitOfTime>(() =>
    bestUnit(campaign.get('repeat_cooldown_seconds')),
  );
  const cooldownDisplayValue = toDisplayValue(
    campaign.get('repeat_cooldown_seconds'),
    cooldownUnit,
  );
  const { t } = useTranslation();

  function handleChangeTargetAddressType(checked?: boolean): void {
    campaign.set({ target_address_type: checked ? 'all' : 'primary' });
  }

  function handleChangeRepeat(newValue?: string): void {
    if (newValue) {
      const resetCooldown =
        newValue === 'every' ? toSeconds(minimumDisplayValues[cooldownUnit], cooldownUnit) : 0;
      const campaignUpdate: Partial<IMailrailAutoSendEmailCampaign> = {
        repeat_allowed: newValue === 'every',
        repeat_cooldown_seconds: resetCooldown,
      };
      if (newValue === 'every') {
        campaignUpdate.bulk_send_enabled = false;
      }
      campaign.set(campaignUpdate);
    }
  }

  function handleChangeCooldownValue(newValue?: number): void {
    if (newValue !== undefined) {
      campaign.set({ repeat_cooldown_seconds: toSeconds(newValue, cooldownUnit) });
    }
  }

  function handleChangeCooldownUnit(option: ITimeUnitOption): void {
    setCooldownUnit(option.id);
    campaign.set({
      repeat_cooldown_seconds: toSeconds(minimumDisplayValues[option.id], option.id),
    });
  }

  function getOptionLabelCooldown(option: ITimeUnitOption): string | undefined {
    return {
      minutes: t('minute', { count: cooldownDisplayValue }),
      hours: t('hour', { count: cooldownDisplayValue }),
      days: t('day', { count: cooldownDisplayValue }),
      weeks: t('week', { count: cooldownDisplayValue }),
    }[option.id];
  }

  function getAddressTypeHelpText(): string {
    if (campaign.isMailrailDrip) {
      return t(
        'Some targeted people may have multiple email addresses. Send triggered emails to every email address stored for every targeted person.',
      );
    }
    return t(
      'Some targeted people may have multiple email addresses. Send this email to every email address stored for every targeted person.',
    );
  }

  function getSendCadenceHelpText(): string {
    if (campaign.isMailrailDrip) {
      return t(
        'Enroll people every time they meet the enrollment trigger, or only the first time.',
      );
    }
    return t('Send your email only the first time the person performs the trigger, or every time.');
  }

  function getCooldownHelpText(): string {
    if (campaign.isMailrailDrip) {
      return t(
        'The minimum amount of time that should pass before someone is able to enroll again.',
      );
    }
    return t(
      'The minimum amount of time that should pass before someone is able to trigger the campaign again.',
    );
  }

  return (
    <>
      {(flags.pinpointSendAllEmails || isDripOrAuto) && (
        <CardV2 width={'full'}>
          <CardHeader title={t('Options')} />
          <CardContent addVerticalGap={true}>
            <>
              {flags.pinpointSendAllEmails && (
                <Checkbox
                  className={styles.sendingOptionCheckbox}
                  // Checkbox is only active for single sends when at least one target group is added
                  disabled={disabled || (includedSegmentIds.length === 0 && !isDripOrAuto)}
                  label={getAddressTypeHelpText()}
                  layout={'well'}
                  onChange={handleChangeTargetAddressType}
                  value={campaign.get('target_address_type') === 'all'}
                />
              )}
              {isDripOrAuto && !isTimeSubtype && (
                <MailrailBulkSendConfig
                  campaign={campaign as MailrailAutoSendEmailCampaign}
                  disabled={disabled}
                />
              )}
              {isDripOrAuto && (
                <Radios
                  disabled={disabled}
                  helpText={getSendCadenceHelpText()}
                  label={campaign.isMailrailDrip ? t('Enrollment cadence') : t('Send cadence')}
                  layout={'block'}
                  name={'send_cadence'}
                  onChange={handleChangeRepeat}
                  options={[
                    {
                      id: 'first',
                      name: campaign.isMailrailDrip
                        ? t('Enroll only the first time')
                        : t('Send only the first time'),
                    },
                    {
                      id: 'every',
                      name: campaign.isMailrailDrip ? t('Enroll every time') : t('Send every time'),
                      disabled: disabled || isTimeSubtype,
                      tooltip: isTimeSubtype
                        ? t('Repeat sending is not possible with a date-triggered campaign.')
                        : undefined,
                    },
                  ]}
                  value={repeatAllowed ? 'every' : 'first'}
                />
              )}
              {isDripOrAuto && repeatAllowed && (
                <NumberInput
                  className={styles.cooldown}
                  clearableClassName={styles.cooldownElement}
                  disabled={disabled}
                  helpText={getCooldownHelpText()}
                  label={t('Cooldown period')}
                  min={minimumDisplayValues[cooldownUnit]}
                  name={'repeat_cooldown_seconds'}
                  onChange={handleChangeCooldownValue}
                  suffix={
                    <Select
                      disabled={disabled}
                      getOptionLabel={getOptionLabelCooldown}
                      getOptionValue={getTimeUnitOptionValue}
                      name={'cooldown_unit'}
                      onSelectSingle={handleChangeCooldownUnit}
                      options={getTimeUnitOptions()}
                      value={getTimeUnitOptions().find((o) => o.id === cooldownUnit)}
                    />
                  }
                  validationError={flattenError(validationErrors.repeat_cooldown_seconds)}
                  value={cooldownDisplayValue}
                />
              )}
            </>
          </CardContent>
        </CardV2>
      )}
    </>
  );
}

export default observer(MailrailCampaignSendingOptions);
